import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FileText,
  Settings2,
  Eye,
  ArrowRight,
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle,
} from "lucide-react";

/**
 * Per-document tuning configuration (matches router schema)
 */
interface DocumentTuning {
  skipRows?: number;
  delimiter?: "," | ";" | "\t" | "|";
  dateColumn?: string;
  amountColumn?: string;
  descriptionColumn?: string;
  vendorHints?: string;
  ignoreColumns?: string[];
  notes?: string;
}

interface TuningParams {
  documents?: Record<string, DocumentTuning>;
  globalHints?: string;
}

interface CSVTuningPanelProps {
  documentIds: number[];
  companyId: number;
  onBack: () => void;
  onStartAnalysis: (tuningParams: TuningParams) => void;
  isStarting?: boolean;
}

export default function CSVTuningPanel({
  documentIds,
  companyId,
  onBack,
  onStartAnalysis,
  isStarting = false,
}: CSVTuningPanelProps) {
  const [activeDocId, setActiveDocId] = useState<number | null>(null);
  const [tuningParams, setTuningParams] = useState<TuningParams>({
    documents: {},
    globalHints: "",
  });

  // Set first CSV document as active when documentIds change
  useEffect(() => {
    if (documentIds.length > 0 && !activeDocId) {
      setActiveDocId(documentIds[0]);
    }
  }, [documentIds, activeDocId]);

  // Fetch preview for active document
  const {
    data: preview,
    isLoading: previewLoading,
    error: previewError,
  } = trpc.intelligence.previewCSV.useQuery(
    {
      documentId: activeDocId!,
      companyId,
      maxLines: 50,
    },
    {
      enabled: !!activeDocId,
    }
  );

  const getDocTuning = (docId: number): DocumentTuning => {
    return tuningParams.documents?.[String(docId)] || {};
  };

  const updateDocTuning = (docId: number, updates: Partial<DocumentTuning>) => {
    setTuningParams(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [String(docId)]: {
          ...prev.documents?.[String(docId)],
          ...updates,
        },
      },
    }));
  };

  const handleStartAnalysis = () => {
    // Clean up empty tuning objects
    const cleanedParams: TuningParams = {
      globalHints: tuningParams.globalHints || undefined,
    };

    if (tuningParams.documents) {
      const docs: Record<string, DocumentTuning> = {};
      for (const [docId, tuning] of Object.entries(tuningParams.documents)) {
        // Only include if has non-empty values
        const hasValues = Object.values(tuning).some(
          v => v !== undefined && v !== "" && v !== 0
        );
        if (hasValues) {
          docs[docId] = tuning;
        }
      }
      if (Object.keys(docs).length > 0) {
        cleanedParams.documents = docs;
      }
    }

    onStartAnalysis(cleanedParams);
  };

  const currentTuning = activeDocId ? getDocTuning(activeDocId) : {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            CSV Tuning
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure how each CSV file is parsed before analysis
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack} disabled={isStarting}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleStartAnalysis} disabled={isStarting}>
            {isStarting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ArrowRight className="h-4 w-4 mr-2" />
            )}
            Start Analysis
          </Button>
        </div>
      </div>

      {/* Document Tabs */}
      <Tabs
        value={String(activeDocId)}
        onValueChange={v => setActiveDocId(parseInt(v))}
      >
        <TabsList className="flex-wrap h-auto gap-1">
          {documentIds.map((docId, idx) => (
            <TabsTrigger key={docId} value={String(docId)} className="text-xs">
              <FileText className="h-3 w-3 mr-1" />
              Doc {idx + 1}
              {getDocTuning(docId).skipRows ? (
                <Badge variant="secondary" className="ml-1 text-xs px-1">
                  tuned
                </Badge>
              ) : null}
            </TabsTrigger>
          ))}
        </TabsList>

        {documentIds.map(docId => (
          <TabsContent key={docId} value={String(docId)} className="mt-4">
            {previewLoading ? (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Loading CSV preview...</span>
                  </div>
                </CardContent>
              </Card>
            ) : previewError ? (
              <Card className="border-destructive">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 text-destructive">
                    <AlertCircle className="h-5 w-5" />
                    <span>
                      {previewError.message || "Failed to load preview"}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ) : preview ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Preview Panel */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Preview: {preview.fileName}
                    </CardTitle>
                    <CardDescription>
                      {preview.totalLines} total lines • {preview.previewLines}{" "}
                      shown • Delimiter:{" "}
                      <code className="bg-muted px-1 rounded">
                        {preview.detectedDelimiter === "\t"
                          ? "TAB"
                          : preview.detectedDelimiter}
                      </code>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Headers */}
                    <div className="mb-3">
                      <Label className="text-xs text-muted-foreground">
                        Detected Headers:
                      </Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {preview.headerRow.map((col, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="text-xs font-mono"
                          >
                            {idx}: {col || "(empty)"}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Line-by-line preview */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="max-h-80 overflow-y-auto font-mono text-xs">
                        {preview.lines.map(line => {
                          const isSkipped =
                            currentTuning.skipRows &&
                            line.lineNumber <= currentTuning.skipRows;
                          return (
                            <div
                              key={line.lineNumber}
                              className={`flex border-b last:border-b-0 ${
                                isSkipped
                                  ? "bg-muted/50 text-muted-foreground line-through"
                                  : line.lineNumber === 1
                                    ? "bg-primary/5 font-semibold"
                                    : ""
                              }`}
                            >
                              <div className="w-10 flex-shrink-0 px-2 py-1 bg-muted text-muted-foreground text-right border-r">
                                {line.lineNumber}
                              </div>
                              <div className="px-2 py-1 overflow-x-auto whitespace-pre">
                                {line.content}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Tuning Controls */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Settings2 className="h-4 w-4" />
                      Parsing Options
                    </CardTitle>
                    <CardDescription>
                      Customize how this CSV is parsed
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Skip Rows */}
                    <div className="space-y-2">
                      <Label htmlFor="skipRows">Skip First N Rows</Label>
                      <Input
                        id="skipRows"
                        type="number"
                        min={0}
                        max={50}
                        placeholder="0"
                        value={currentTuning.skipRows || ""}
                        onChange={e =>
                          updateDocTuning(docId, {
                            skipRows: parseInt(e.target.value) || 0,
                          })
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        Skip metadata rows before the header (e.g., "start
                        parsing on line 5")
                      </p>
                    </div>

                    {/* Delimiter */}
                    <div className="space-y-2">
                      <Label htmlFor="delimiter">Delimiter</Label>
                      <Select
                        value={
                          currentTuning.delimiter || preview.detectedDelimiter
                        }
                        onValueChange={v =>
                          updateDocTuning(docId, {
                            delimiter: v as "," | ";" | "\t" | "|",
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value=",">Comma (,)</SelectItem>
                          <SelectItem value=";">Semicolon (;)</SelectItem>
                          <SelectItem value="\t">Tab</SelectItem>
                          <SelectItem value="|">Pipe (|)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Column Mapping */}
                    <div className="space-y-2">
                      <Label>Column Mapping (optional)</Label>
                      <div className="grid grid-cols-1 gap-2">
                        <div>
                          <Label
                            htmlFor="dateColumn"
                            className="text-xs text-muted-foreground"
                          >
                            Date Column
                          </Label>
                          <Input
                            id="dateColumn"
                            placeholder="e.g., 'Date' or '0'"
                            value={currentTuning.dateColumn || ""}
                            onChange={e =>
                              updateDocTuning(docId, {
                                dateColumn: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div>
                          <Label
                            htmlFor="amountColumn"
                            className="text-xs text-muted-foreground"
                          >
                            Amount Column
                          </Label>
                          <Input
                            id="amountColumn"
                            placeholder="e.g., 'Amount' or '3'"
                            value={currentTuning.amountColumn || ""}
                            onChange={e =>
                              updateDocTuning(docId, {
                                amountColumn: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div>
                          <Label
                            htmlFor="descriptionColumn"
                            className="text-xs text-muted-foreground"
                          >
                            Description Column
                          </Label>
                          <Input
                            id="descriptionColumn"
                            placeholder="e.g., 'Description' or '2'"
                            value={currentTuning.descriptionColumn || ""}
                            onChange={e =>
                              updateDocTuning(docId, {
                                descriptionColumn: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>

                    {/* Vendor Hints */}
                    <div className="space-y-2">
                      <Label htmlFor="vendorHints">
                        Vendor Extraction Hints
                      </Label>
                      <Textarea
                        id="vendorHints"
                        placeholder="e.g., 'Description contains bank routing info, not vendor. Vendor name appears after the date.'"
                        value={currentTuning.vendorHints || ""}
                        onChange={e =>
                          updateDocTuning(docId, {
                            vendorHints: e.target.value,
                          })
                        }
                        rows={3}
                      />
                      <p className="text-xs text-muted-foreground">
                        Help the AI understand how to extract vendor names from
                        this file
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : null}
          </TabsContent>
        ))}
      </Tabs>

      {/* Global Hints */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Global Analysis Hints</CardTitle>
          <CardDescription>
            Instructions that apply to all documents in this batch
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="e.g., 'These are credit card statements. Payment descriptions often include merchant category codes.'"
            value={tuningParams.globalHints || ""}
            onChange={e =>
              setTuningParams(prev => ({
                ...prev,
                globalHints: e.target.value,
              }))
            }
            rows={2}
          />
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-success" />
          <span className="text-sm">
            Ready to analyze {documentIds.length} document
            {documentIds.length !== 1 ? "s" : ""}
          </span>
        </div>
        <Button onClick={handleStartAnalysis} disabled={isStarting}>
          {isStarting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <ArrowRight className="h-4 w-4 mr-2" />
          )}
          Start Batch Analysis
        </Button>
      </div>
    </div>
  );
}
